import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';

const isValidInternationalPhone = (value: string) => {
  const normalized = (value ?? '').trim();

  if (!normalized) return false;
  if (!/^\+[1-9]\d{1,14}$/.test(normalized)) return false;

  return isValidPhoneNumber(normalized);
};

export const loginSchema = z.object({
  username: z.string().nonempty('Username is required'),
  password: z.string().nonempty('Password is required'),
});

// Register

export const registerSchema = z.object({
  username: z
    .string()
    .min(4, 'Username must be 4 to 15 characters')
    .max(15, 'Username must be 4 to 15 characters')
    .regex(/^[a-zA-Z0-9]+$/, 'Username must be alphanumeric'),
  mobile: z
    .string({
      required_error: 'Mobile number is required',
      invalid_type_error: 'Mobile number is required',
    })
    .trim()
    .nonempty('Mobile number is required')
    .refine(
      (value) => isValidInternationalPhone(value),
      'Enter a valid phone number with country code'
    ),
  password: z
    .string()
    .min(6, 'Password must be 6 to 20 characters')
    .max(20, 'Password must be 6 to 20 characters')
    .regex(
      /^[a-zA-Z0-9@._#$!%&*-]+$/,
      'Only letters (A-Z, a-z), numbers (0-9), and these special characters are allowed: @ . _ # $ ! % & * -'
    ),
  referralCode: z.string().optional(),
});

// Forgot Password

export const phoneSchema = z.object({
  mobile: z
    .string()
    .min(1, 'Mobile number is required')
    .max(20, 'Mobile number must be at most 20 digits')
    .regex(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number'),
});

export const resetPasswordSchema = z
  .object({
    otp: z.string().min(1, 'Verification code is required').min(6, 'OTP must be at least 6 digits'),
    newPassword: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),

    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(14, 'Password must be at most 14 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),

    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
